import { productBuilder } from '@noths/polaris-client-data-builders';
import { createSlice } from '@reduxjs/toolkit';

import { categoryBuilder } from 'src/builders/category';
import { logger } from 'src/utils';
import { addLoadingProducts } from './actions';
import { requestFeaturedProduct, requestProducts, updateFilters } from './thunks';
import { ProductsState } from './types';

const PRODUCTS_PER_PAGE = 60;
const CLIENT_ABORT_FETCH_ERROR = 'FetchError:CLIENT_ABORT';

export const initialState: Readonly<ProductsState> = {
  searchTerm: '',
  category: categoryBuilder(),
  currencyCode: 'GBP',
  items: {},
  currentPage: 0,
  totalPages: 0,
  totalProducts: 0,
  lastRequestedPageNumber: 0,
  productsReturnedCount: 0,
  queryType: '',
  navigation: [],
  isLoading: false,
  isUpdateFiltersLoading: false,
  isClientRequest: false,
  featuredProduct: null,
};

export const { actions, reducer } = createSlice({
  name: 'products',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(requestProducts.fulfilled, (state, { meta, payload }) => {
        const {
          replaceProducts = true,
          query: { page },
        } = meta.arg;

        const lastRequestedPageNumber = Number(page ?? 1);
        const items = replaceProducts
          ? { ...payload.items }
          : {
              ...state.items,
              ...payload.items,
            };

        const {
          currencyCode,
          productsReturnedCount,
          queryType,
          currentPage,
          totalPages,
          totalProducts,
          category,
          partner,
          navigation,
          searchTerm = state.searchTerm,
        } = payload;

        return {
          ...state,
          currencyCode,
          searchTerm,
          productsReturnedCount,
          queryType,
          currentPage:
            replaceProducts || currentPage > state.currentPage ? currentPage : state.currentPage,
          totalPages,
          totalProducts,
          lastRequestedPageNumber,
          category,
          partner,
          navigation,
          items,
          isLoading: false,
          error: null,
        } as ProductsState;
      })

      .addCase(requestProducts.pending, (state, { meta }) => {
        const { isClientRequest = false } = meta.arg;
        state.isLoading = true;
        state.isClientRequest = isClientRequest;
      })
      .addCase(requestProducts.rejected, (state, action) => {
        if (action.payload?.redirectUrl) {
          state.redirectUrl = action.payload.redirectUrl;
          return;
        }

        logger.error('Error requesting products', action.error);

        // Handle client side aborted requests differently to server side aborted requests.
        // Client side aborted requests are due to user changing products query (via UI).
        // Server side aborted requests are due to timeout.
        if (action.error.name === CLIENT_ABORT_FETCH_ERROR) {
          return;
        }

        // Handle both server abort (action.error.name === 'FetchError:SERVER_TIMEOUT') and unhandled errors.
        // These could be handled differently in future if desired.
        state.isLoading = false;
        state.items = {};
        state.error = action.error;
      })
      .addCase(updateFilters.pending, (state) => {
        state.isUpdateFiltersLoading = true;
      })
      .addCase(updateFilters.fulfilled, (state) => {
        state.isUpdateFiltersLoading = false;
      })
      .addCase(addLoadingProducts, (state, { payload: page }) => {
        state.items[Number(page)] = Array.from({ length: PRODUCTS_PER_PAGE }, (_, index) =>
          productBuilder({
            code: index,
            isLoading: true,
          }),
        );
      })
      .addCase(requestFeaturedProduct.fulfilled, (state, { payload }) => {
        state.featuredProduct = payload;
      })
      .addCase(requestFeaturedProduct.rejected, (_, action) => {
        logger.error('Error requesting featured product', action.error);
      });
  },
});
